import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Section";
import { useContent } from "@/i18n";
import { Shape3D } from "@/components/Objects3D";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const { ui, lang } = useContent();
  const pick = (p, field) => (lang === "de" && p[`${field}_de`] ? p[`${field}_de`] : p[field]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/journal`)
      .then((r) => r.json())
      .then(setPosts)
      .catch(() => {});
  }, []);

  const featured = posts.find((p) => p.featured) || posts[0];
  const rest = posts.filter((p) => featured && p.slug !== featured.slug);

  return (
    <main className="min-h-screen bg-cream relative overflow-hidden" data-testid="journal-page">
      <Shape3D shape="star" blue size={48} className="absolute right-[8%] top-28 opacity-80" />
      <div className="max-w-7xl mx-auto px-6 pt-10 pb-28 relative">
        <Link
          to="/"
          data-testid="journal-back-link"
          className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-cocoa hover:text-choco transition-colors duration-300"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          {ui.journal.back}
        </Link>

        <Reveal>
          <div className="mt-14 flex items-baseline gap-4">
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-cobalt">{ui.journal.kicker}</span>
            <span className="h-px w-16 bg-choco/30" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-cocoa">{ui.journal.sub}</span>
          </div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tighter leading-none">
            {ui.journal.titleA} <span className="italic text-cobalt">{ui.journal.titleB}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-cocoa">{ui.journal.intro}</p>
        </Reveal>

        {featured && (
          <Reveal delay={0.15}>
            <Link to={`/journal/${featured.slug}`} className="group block mt-16 rounded-2xl border border-line bg-white shadow-[0_26px_60px_-34px_rgba(51,38,25,0.35)] overflow-hidden" data-testid="journal-featured-post">
              <div className="grid lg:grid-cols-2">
                <div className="overflow-hidden aspect-[16/10] lg:aspect-auto">
                  <img src={featured.image} alt={pick(featured, "title")} className="frame-zoom w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-between">
                  <div>
                    <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-cobalt">
                      {pick(featured, "tag")} · {featured.date} · {pick(featured, "read_time")}
                    </p>
                    <h2 className="mt-5 font-serif text-3xl md:text-4xl tracking-tight leading-tight group-hover:text-cobalt transition-colors duration-400">
                      {pick(featured, "title")}
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-cocoa">{pick(featured, "excerpt")}</p>
                  </div>
                  <span className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-choco">
                    {ui.journal.readEssay} <ArrowUpRight size={14} className="text-cobalt transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {rest.map((post, i) => (
            <Reveal key={post.slug} delay={0.06 * i}>
              <Link
                to={`/journal/${post.slug}`}
                className={`group flex flex-col h-full rounded-2xl border border-line bg-white overflow-hidden shadow-[0_20px_44px_-30px_rgba(51,38,25,0.3)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-500 ${i === 1 ? "-rotate-1" : "rotate-1"}`}
                data-testid={`journal-card-${i}`}
              >
                <div className="overflow-hidden aspect-[16/10]">
                  <img src={post.image} alt={pick(post, "title")} className="frame-zoom w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-cobalt">
                    {pick(post, "tag")} · {post.date}
                  </p>
                  <h2 className="mt-4 font-serif text-2xl tracking-tight leading-snug group-hover:text-cobalt transition-colors duration-400">
                    {pick(post, "title")}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-cocoa flex-1">{pick(post, "excerpt")}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold">
                    {ui.journal.read} <ArrowUpRight size={13} className="text-cobalt" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </main>
  );
}
